import { ref } from "vue";
import apiClient from "@/services/api";

export const PERIOD_DURATION = { monthly: 30, "3months": 90, yearly: 365 };

// Plan limit convention (matches the backend, authentication/plans.py):
//   0         = not included on the plan
//   >= 99999999 = unlimited
// Lifetime fields are the exception: 0 means "no lifetime cap, use the
// monthly/periodic limit instead".
export const UNLIMITED = 99999999;

// Fallback defaults per plan type — used only when the API hasn't returned
// a field yet, or the plans request fails outright, so pricing pages never
// render blank/broken. Keep these in sync with authentication/management/
// commands/create_subscription_plans.py if the real plan config changes.
const PLAN_DEFAULTS = {
  free: {
    price: 0,
    max_saved_jobs: UNLIMITED,
    max_job_filter_presets: 1,
    max_ai_cover_letters_per_month: 5,
    max_ai_cover_letters_lifetime: 0,
    max_auto_apply_lifetime: 0,
    max_auto_apply_per_month: 0,
    max_manual_apply_per_week: UNLIMITED,
    has_telegram_alerts: false,
    has_email_notifications: true,
    has_priority_support: false,
  },
  basic: {
    price: 199,
    max_saved_jobs: UNLIMITED,
    max_job_filter_presets: 5,
    max_ai_cover_letters_per_month: 250,
    max_ai_cover_letters_lifetime: 0,
    max_auto_apply_lifetime: 0,
    max_auto_apply_per_month: 0,
    max_manual_apply_per_week: UNLIMITED,
    has_telegram_alerts: true,
    has_email_notifications: true,
    has_priority_support: false,
  },
  premium: {
    price: 299,
    max_saved_jobs: UNLIMITED,
    max_job_filter_presets: 10,
    max_ai_cover_letters_per_month: 500,
    max_ai_cover_letters_lifetime: 0,
    max_auto_apply_lifetime: 0,
    max_auto_apply_per_month: UNLIMITED,
    max_manual_apply_per_week: UNLIMITED,
    has_telegram_alerts: true,
    has_email_notifications: true,
    has_priority_support: true,
  },
};

export function resolvePlanFeatures(plan, planType) {
  const defaults = PLAN_DEFAULTS[planType] || PLAN_DEFAULTS.free;
  return {
    price: plan?.price ?? defaults.price,
    max_saved_jobs: plan?.max_saved_jobs ?? defaults.max_saved_jobs,
    max_job_filter_presets:
      plan?.max_job_filter_presets ?? defaults.max_job_filter_presets,
    max_ai_cover_letters_per_month:
      plan?.max_ai_cover_letters_per_month ?? defaults.max_ai_cover_letters_per_month,
    max_ai_cover_letters_lifetime:
      plan?.max_ai_cover_letters_lifetime ?? defaults.max_ai_cover_letters_lifetime,
    max_auto_apply_lifetime:
      plan?.max_auto_apply_lifetime ?? defaults.max_auto_apply_lifetime,
    max_auto_apply_per_month:
      plan?.max_auto_apply_per_month ?? defaults.max_auto_apply_per_month,
    max_manual_apply_per_week:
      plan?.max_manual_apply_per_week ?? defaults.max_manual_apply_per_week,
    has_telegram_alerts:
      plan?.has_telegram_alerts ?? defaults.has_telegram_alerts,
    has_email_notifications:
      plan?.has_email_notifications ?? defaults.has_email_notifications,
    has_priority_support:
      plan?.has_priority_support ?? defaults.has_priority_support,
  };
}

export function formatPeso(price) {
  if (price == null || price === "") return "-";
  const n = Number(price);
  if (Number.isNaN(n)) return "-";
  return `₱${n.toLocaleString("en-PH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

// Formats one plan limit. `unit` is e.g. "/ month". Returns null when the
// limit is 0 (not included) so callers can omit the row or show "Not included".
function describeLimit(n, unit) {
  const v = Number(n) || 0;
  if (v >= UNLIMITED) return { kind: "unlimited" };
  if (v <= 0) return { kind: "none" };
  return { kind: "count", text: `${v} ${unit}` };
}

export function isUnlimited(n) {
  return Number(n) >= UNLIMITED;
}

// Auto Apply: a lifetime cap (Free) wins over the monthly limit.
function autoApplyLimit(f) {
  if (f.max_auto_apply_lifetime > 0) return { kind: "count", text: `${f.max_auto_apply_lifetime} total`, total: f.max_auto_apply_lifetime };
  return describeLimit(f.max_auto_apply_per_month, "/ month");
}
function coverLetterLimit(f) {
  if (f.max_ai_cover_letters_lifetime > 0) return { kind: "count", text: `${f.max_ai_cover_letters_lifetime} total` };
  return describeLimit(f.max_ai_cover_letters_per_month, "/ month");
}

// Compact "N / month" / "N total" / "Unlimited" / "Not included" cells — comparison table.
function toCell(l) {
  if (l.kind === "unlimited") return "Unlimited";
  if (l.kind === "none") return "Not included";
  return l.text;
}
export function coverLettersCell(f) {
  return toCell(coverLetterLimit(f));
}
export function autoApplyCell(f) {
  return toCell(autoApplyLimit(f));
}
export function directApplyCell(f) {
  return toCell(describeLimit(f.max_manual_apply_per_week, "/ week"));
}
export function savedJobsCell(f) {
  if (!f.max_saved_jobs || f.max_saved_jobs >= UNLIMITED) return "Unlimited";
  return `${f.max_saved_jobs}`;
}

// "Sentence" style labels — pricing card feature lists. Return null when the
// feature is not included on the plan (callers show it as not included).
export function coverLettersLabel(f) {
  if (f.max_ai_cover_letters_lifetime > 0) return `Up to ${f.max_ai_cover_letters_lifetime} AI Cover Letters`;
  const l = describeLimit(f.max_ai_cover_letters_per_month, "");
  if (l.kind === "none") return null;
  if (l.kind === "unlimited") return "Unlimited AI Cover Letters";
  return `${f.max_ai_cover_letters_per_month} AI Cover Letters/mo`;
}
export function autoApplyLabel(f) {
  const l = autoApplyLimit(f);
  if (l.kind === "none") return null;
  if (l.kind === "unlimited") return "Unlimited Auto-Apply";
  if (l.total) return `Up to ${l.total} Auto-Apply`;
  return `${f.max_auto_apply_per_month} Auto-Apply/mo`;
}
export function directApplyLabel(f) {
  const l = describeLimit(f.max_manual_apply_per_week, "");
  if (l.kind === "none") return null;
  if (l.kind === "unlimited") return "Unlimited Direct Apply";
  return `${f.max_manual_apply_per_week} Direct Apply/week`;
}

// Short marketing bullets — HomePage pricing teaser. null = not included (omit).
export function coverLettersBullet(f) {
  if (f.max_ai_cover_letters_lifetime > 0) return `Up to ${f.max_ai_cover_letters_lifetime} AI cover letters`;
  const l = describeLimit(f.max_ai_cover_letters_per_month, "");
  if (l.kind === "none") return null;
  if (l.kind === "unlimited") return "Unlimited AI cover letters";
  return `${f.max_ai_cover_letters_per_month} AI cover letters / month`;
}
export function autoApplyBullet(f) {
  const l = autoApplyLimit(f);
  if (l.kind === "none") return null;
  if (l.kind === "unlimited") return "Unlimited Auto Apply";
  if (l.total) return `Up to ${l.total} Auto Apply`;
  return `${f.max_auto_apply_per_month} Auto Apply / month`;
}
export function directApplyBullet(f) {
  const l = describeLimit(f.max_manual_apply_per_week, "");
  if (l.kind === "none") return null;
  if (l.kind === "unlimited") return "Unlimited Direct Apply";
  return `${f.max_manual_apply_per_week} Direct Apply / week`;
}

export function useSubscriptionPlans() {
  const plans = ref([]);
  const plansLoading = ref(true);
  const plansError = ref(null);

  const fetchSubscriptionPlans = async () => {
    try {
      plansLoading.value = true;
      plansError.value = null;
      const res = await apiClient.get("/auth/subscription/plans/");
      plans.value = res.data;
    } catch (err) {
      plansError.value = "Failed to load subscription plans.";
    } finally {
      plansLoading.value = false;
    }
  };

  const findPlan = (planType, durationDays = 30) =>
    plans.value.find(
      (p) => p.plan_type === planType && !p.is_test_plan && (p.duration_days ?? 30) === durationDays
    ) || null;

  return { plans, plansLoading, plansError, fetchSubscriptionPlans, findPlan };
}
